/**
 * Application multi evenement e.labriquerie.net.
 * src/i18n/fr.ficheEntretien.ts
 *
 * Rôle :
 *   Textes de la FICHE D'ENTRETIEN (demande de Mika du 2026-10-04) : le scan
 *   d'un badge ouvre la fiche de la personne, et les anciennes « rencontres »
 *   s'affichent désormais sous ce seul nom. Un seul nom à l'écran : « Fiche
 *   d'entretien », au pluriel « Fiches d'entretien ». Le mot « Entretiens »
 *   seul reste celui des entretiens PLANIFIÉS de l'offre premium, qu'une
 *   fiche affiche sous le nom « Entretien planifié ».
 *
 *   Les écrans repris (liste, Mon stand, scanner) lisent leurs nouveaux
 *   libellés ICI, sous des clés neuves : les anciennes clés du noyau restent
 *   en place pour les écrans qui ne sont pas de ce chantier.
 *
 *   Règles : jamais le nom de l'événement ni son type en dur, jamais le mot
 *   désignant les sessions du programme, aucun tiret cadratin, apostrophes
 *   typographiques. Vérifié par src/i18n/__tests__/fr.ficheEntretien.test.ts.
 *
 * Mikael - chef de projet - 2026-10-04
 */

export const messagesFicheEntretienFr: Record<string, string> = {
  // ───── La fiche ─────────────────────────────────────────────────────────
  'ficheEntretien.titre': 'Fiche d’entretien',
  'ficheEntretien.chargement': 'Ouverture de la fiche…',
  'ficheEntretien.erreur': 'La fiche n’a pas pu être chargée. Vérifiez le réseau et réessayez.',
  'ficheEntretien.reessayer': 'Réessayer',
  'ficheEntretien.interdite': 'Cette fiche appartient à un autre compte de votre entreprise.',
  'ficheEntretien.introuvable': 'Cette fiche n’existe plus.',
  'ficheEntretien.enAttente.titre': 'Fiche en attente de réseau',
  'ficheEntretien.enAttente.texte':
    'Le scan est gardé sur ce téléphone et partira dès le retour du réseau. Vous pouvez déjà noter l’intérêt, la note et la décision : ils partiront avec lui.',
  'ficheEntretien.entete.heure': 'À {heure}',
  'ficheEntretien.entete.par': 'par {nom}',
  'ficheEntretien.entete.parMoi': 'par vous',
  'ficheEntretien.entete.reprise': 'Fiche rouverte : ce badge était déjà scanné par vous aujourd’hui.',
  'ficheEntretien.mineur': 'Mineur',

  // Entretien planifié rattaché
  'ficheEntretien.planifie.titre': 'Entretien planifié',
  'ficheEntretien.planifie.creneau': '{debut} à {fin}',
  'ficheEntretien.planifie.salle': 'Salle : {salle}',
  'ficheEntretien.planifie.partage':
    'Intérêt, décision et notes sont ceux de l’évaluation de cet entretien : modifier ici, c’est modifier l’évaluation.',
  'ficheEntretien.planifie.noteFiche': 'Note prise avant le rattachement : {note}',

  // Points forts
  'ficheEntretien.points.titre': 'Points forts du CV',
  'ficheEntretien.points.ia': 'Résumé assisté',
  'ficheEntretien.points.iaEnCours': 'Résumé en préparation…',
  'ficheEntretien.points.vide': 'Aucun point fort n’a pu être lu.',
  'ficheEntretien.points.cvIndisponible': 'Pas de CV partagé avec votre entreprise.',
  'ficheEntretien.points.cvIllisible':
    'Le CV n’a pas de texte lisible (document scanné ou photo) : ouvrez-le pour le lire.',
  'ficheEntretien.points.type.poste': 'Poste',
  'ficheEntretien.points.type.diplome': 'Diplôme',
  'ficheEntretien.points.type.habilitations': 'Habilitations',
  'ficheEntretien.points.type.langues': 'Langues',
  'ficheEntretien.points.type.competences': 'Compétences',
  'ficheEntretien.points.type.recherche': 'Recherche',
  'ficheEntretien.points.type.offre': 'Votre offre',
  'ficheEntretien.points.type.ia': 'Point fort',
  'ficheEntretien.memoriser': 'Mémoriser dans la fiche',
  'ficheEntretien.memoriser.aria': 'Mémoriser ces points forts dans la fiche, avec l’heure',
  'ficheEntretien.memoriser.enCours': 'Mémorisation…',
  'ficheEntretien.memoriser.erreur': 'Mémorisation impossible pour le moment. Réessayez.',
  'ficheEntretien.memoriser.horsLigne': 'Mémorisation possible au retour du réseau.',
  'ficheEntretien.memorise': 'Mémorisé à {heure}',
  'ficheEntretien.memorise.titre': 'Points forts mémorisés',
  'ficheEntretien.memorise.aide': 'Figés à {heure} : ils restent, même si le CV change ou est retiré.',

  // Intérêt, note, décision
  'ficheEntretien.interet': 'Intérêt',
  'ficheEntretien.interet.niveau': '{n, plural, one {# étoile sur 5} other {# étoiles sur 5}}',
  'ficheEntretien.interet.effacer': 'Effacer l’intérêt',
  'ficheEntretien.note': 'Note libre',
  'ficheEntretien.note.placeholder': 'Ce que vous retenez de l’échange…',
  'ficheEntretien.note.ajout': 'Ajouter une note à l’entretien',
  'ficheEntretien.note.ajout.placeholder': 'Elle s’ajoutera, horodatée, aux notes de l’entretien.',
  'ficheEntretien.note.compteur': '{n} sur {max}',
  'ficheEntretien.notes.fil': 'Notes de l’entretien',
  'ficheEntretien.decision': 'Décision',
  'ficheEntretien.decision.aucune': 'Pas encore décidé',
  'ficheEntretien.decision.a_revoir': 'À revoir',
  'ficheEntretien.decision.proposition': 'Proposition à faire',
  'ficheEntretien.decision.ne_pas_retenir': 'Ne pas retenir',
  'ficheEntretien.decision.interetRequis': 'Choisissez d’abord l’intérêt : l’évaluation de l’entretien planifié l’exige.',

  // Enregistrement
  'ficheEntretien.enregistrer': 'Enregistrer la fiche',
  'ficheEntretien.enregistrer.enCours': 'Enregistrement…',
  'ficheEntretien.etat.enregistre': 'Enregistré',
  'ficheEntretien.etat.modifie': 'Modifications non enregistrées',
  'ficheEntretien.etat.file': 'Gardé sur ce téléphone, envoi au retour du réseau',
  'ficheEntretien.etat.erreur': 'Non enregistré. Votre saisie est gardée : réessayez.',
  'ficheEntretien.enregistre.toast': 'Fiche enregistrée',
  'ficheEntretien.file.toast': 'Fiche gardée sur ce téléphone, envoi au retour du réseau',
  'ficheEntretien.fermer': 'Fermer la fiche',
  'ficheEntretien.scannerSuivant': 'Enregistrer et scanner le suivant',

  // CV
  'ficheEntretien.cv.ouvrir': 'Ouvrir le CV',
  'ficheEntretien.cv.ouvrir.aria': 'Ouvrir le CV de {nom}',
  'ficheEntretien.cv.absent': 'CV non partagé',

  // Autres entretiens, nouvel entretien
  'ficheEntretien.autres.titre': 'Autres entretiens avec cette personne',
  'ficheEntretien.autres.vide': 'Aucun autre entretien de votre entreprise avec cette personne.',
  'ficheEntretien.autres.ligne': '{heure}, {par}',
  'ficheEntretien.autres.ouvrir': 'Ouvrir la fiche de {heure}',
  'ficheEntretien.nouvel': '+ Nouvel entretien',
  'ficheEntretien.nouvel.aide': 'Pour un deuxième échange, ou avec un autre recruteur : une fiche neuve, la présente reste telle quelle.',
  'ficheEntretien.nouvel.erreur': 'La nouvelle fiche n’a pas pu être créée. Réessayez.',
  'ficheEntretien.nouvel.cree': 'Nouvelle fiche ouverte',

  // ───── La liste (ancienne page « Mes rencontres ») ──────────────────────
  'fichesEntretien.titre': 'Fiches d’entretien',
  'fichesEntretien.lead':
    'Une fiche par échange, créée au scan du badge. Touchez une fiche pour la compléter : intérêt, note, décision, points forts du CV.',
  'fichesEntretien.retour': 'Retour à mon stand',
  'fichesEntretien.export': 'Exporter (CSV)',
  'fichesEntretien.verification': 'Vérification de votre accès…',
  'fichesEntretien.chargement': 'Chargement des fiches…',
  'fichesEntretien.erreur': 'Les fiches n’ont pas pu être chargées.',
  'fichesEntretien.vide': 'Aucune fiche pour ce filtre. Les fiches se créent en scannant les badges.',
  'fichesEntretien.compte': '{n, plural, one {# fiche} other {# fiches}}',
  'fichesEntretien.filtres.aria': 'Filtrer par décision',
  'fichesEntretien.filtre.toutes': 'Toutes',
  'fichesEntretien.filtre.aucune': 'Sans décision',
  'fichesEntretien.recherche.label': 'Rechercher une personne',
  'fichesEntretien.recherche.placeholder': 'Prénom, nom ou structure',
  'fichesEntretien.recherche.bouton': 'Rechercher',
  'fichesEntretien.par': 'Par {nom}',
  'fichesEntretien.parMoi': 'Par moi',
  'fichesEntretien.planifie': 'Entretien planifié',
  'fichesEntretien.memorise': 'Points mémorisés',
  'fichesEntretien.cv': 'CV',
  'fichesEntretien.interet': '{n} sur 5',
  'fichesEntretien.ouvrir': 'Ouvrir la fiche de {nom}, {heure}',
  'fichesEntretien.notExhibitor': 'Cet espace est réservé aux entreprises inscrites.',

  // ───── Mon stand et Ma journée (écrans repris) ──────────────────────────
  'fichesEntretien.lien': 'Fiches d’entretien',
  'fichesEntretien.recentes.titre': 'Dernières fiches d’entretien',
  'fichesEntretien.recentes.ouvrir': 'Ouvrir',
  'fichesEntretien.recentes.ouvrir.aria': 'Ouvrir la fiche de {nom}',
  'fichesEntretien.completer': 'Compléter mes fiches d’entretien',
  'fichesEntretien.noteTronquee': '(note complète dans la fiche d’entretien)',
};
